import React, { useState, useEffect, useRef } from 'react';
import {
    FiArrowRight, FiCheckCircle, FiAlertCircle, FiCamera, FiFileText,
    FiChevronDown, FiUser, FiPlus, FiCalendar, FiEdit2, FiTrash2, FiX
} from 'react-icons/fi';
import { LuFileClock, LuCopy } from 'react-icons/lu';

export default function AdditionalChecksComponent() {
    // --- Existing State ---
    const [additionalChecks, setAdditionalChecks] = useState([
        { id: 1, title: 'Check Walk-in Fridge', description: 'Ensure temperature is under 4°C.', frequency: 'Daily', mandatory: true, status: 'pass' }
    ]);

    // --- Modal Specific States ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
    const [editingId, setEditingId] = useState(null);
    const [formTitle, setFormTitle] = useState('');
    const [formDescription, setFormDescription] = useState('');

    // --- Handlers ---
    const handleAddNewCheck = () => {
        setModalMode('add');
        setEditingId(null);
        setFormTitle('');
        setFormDescription('');
        setIsModalOpen(true);
    };

    const handleEditCheck = (row) => {
        setModalMode('edit');
        setEditingId(row.id);
        setFormTitle(row.title);
        setFormDescription(row.description);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setFormTitle('');
        setFormDescription('');
    };

    const handleSaveCheck = (e) => {
        e.preventDefault();
        if (!formTitle.trim()) return;

        if (modalMode === 'add') {
            const newCheck = {
                id: Date.now(), // Simple unique ID generation
                title: formTitle,
                description: formDescription,
                frequency: 'Daily', // Default fallback
                mandatory: false,
                status: null
            };
            setAdditionalChecks([...additionalChecks, newCheck]);
        } else {
            setAdditionalChecks(additionalChecks.map(item =>
                item.id === editingId
                    ? { ...item, title: formTitle, description: formDescription }
                    : item
            ));
        }
        handleCloseModal();
    };

    const handleAdditionalDelete = (id) => {
        setAdditionalChecks(additionalChecks.filter(item => item.id !== id));
    };

    const handleAdditionalMandatoryToggle = (id) => {
        setAdditionalChecks(additionalChecks.map(item =>
            item.id === id ? { ...item, mandatory: !item.mandatory } : item
        ));
    };

    const handleAdditionalStatusChange = (id, status) => {
        setAdditionalChecks(additionalChecks.map(item =>
            item.id === id ? { ...item, status: status } : item
        ));
    };

    return (
        <div className="relative">
            <div className="w-full bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden font-sans">

                {/* Header Block */}
                <div className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-50">
                    <div>
                        <div className="flex items-center gap-1.5">
                            <h2 className="text-sm font-bold text-slate-900">Additional Checks</h2>
                            <span className="text-xs font-normal text-slate-400">(Optional)</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">Add tasks that are specific to your restaurant.</p>
                    </div>
                    <div className="flex items-center gap-2.5 self-end sm:self-center">
                        <button
                            type="button"
                            onClick={handleAddNewCheck}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-blue-600 bg-white hover:bg-slate-50 transition-colors"
                        >
                            <FiPlus className="text-sm" />
                            <span>Add new check</span>
                        </button>
                        <button type="button" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-700 bg-white hover:bg-slate-50 transition-colors">
                            <FiFileText className="text-slate-400 text-sm" />
                            <span>Import from template</span>
                            <FiChevronDown className="text-slate-400 text-xs ml-0.5" />
                        </button>
                    </div>
                </div>

                {/* Data Table View */}
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-left">
                        <thead>
                            <tr className="border-b border-slate-50 bg-slate-50/40 text-[11px] font-semibold text-slate-500 tracking-wide uppercase">
                                <th className="py-3 px-4 min-w-[250px]">Check Item</th>
                                <th className="py-3 px-4 w-40">Frequency</th>
                                <th className="py-3 px-4 w-48 text-center">Mandatory for this restaurant</th>
                                <th className="py-3 px-5 w-64 text-center">Status</th>
                                <th className="py-3 px-5 w-24"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {additionalChecks.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="py-12 text-center text-xs text-slate-400 font-medium">
                                        No additional check items added yet. Click "Add new check" below to begin.
                                    </td>
                                </tr>
                            ) : (
                                additionalChecks.map((row) => (
                                    <tr key={row.id} className="hover:bg-slate-50/30 transition-colors group">
                                        {/* Details Column */}
                                        <td className="py-4 px-4">
                                            <div>
                                                <h4 className="text-xs font-bold text-slate-800 leading-tight">{row.title}</h4>
                                                <p className="text-[11px] text-slate-400 mt-0.5 leading-normal">{row.description}</p>
                                            </div>
                                        </td>

                                        {/* Frequency Dropdown Button */}
                                        <td className="py-4 px-4">
                                            <div className="inline-flex items-center justify-between w-28 px-2 py-1 rounded-lg border text-xs font-medium cursor-pointer bg-green-50/30 border-green-200 text-green-700 hover:bg-green-50">
                                                <div className="flex items-center gap-1.5">
                                                    <FiCalendar className="text-xs" />
                                                    <span>{row.frequency}</span>
                                                </div>
                                                <FiChevronDown className="text-[10px] opacity-70" />
                                            </div>
                                        </td>

                                        {/* Mandatory Toggle Button Checkbox */}
                                        <td className="py-4 px-4 text-center">
                                            <button
                                                type="button"
                                                onClick={() => handleAdditionalMandatoryToggle(row.id)}
                                                className={`inline-flex items-center justify-center w-4 h-4 rounded border transition-all ${row.mandatory ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'
                                                    }`}
                                            >
                                                {row.mandatory && <span className="text-[10px]">✓</span>}
                                            </button>
                                        </td>

                                        {/* Pass / Issue Dual Buttons */}
                                        <td className="py-4 px-5">
                                            <div className="flex items-center justify-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleAdditionalStatusChange(row.id, 'pass')}
                                                    className={`flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-semibold w-24 transition-all ${row.status === 'pass' ? 'bg-green-600 border-green-600 text-white' : 'bg-white border-green-200 text-green-700'
                                                        }`}
                                                >
                                                    <FiCheckCircle className="text-xs" />
                                                    <span>Pass</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleAdditionalStatusChange(row.id, 'issue')}
                                                    className={`flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-semibold w-24 transition-all ${row.status === 'issue' ? 'bg-red-600 border-red-600 text-white' : 'bg-white border-red-200 text-red-600'
                                                        }`}
                                                >
                                                    <FiAlertCircle className="text-xs" />
                                                    <span>Issue</span>
                                                </button>
                                            </div>
                                        </td>

                                        {/* Action Buttons Row */}
                                        <td className="py-4 px-5 text-right">
                                            <div className="flex items-center justify-end gap-3 text-slate-400 opacity-60 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditCheck(row)}
                                                    className="hover:text-slate-600"
                                                >
                                                    <FiEdit2 className="text-xs" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleAdditionalDelete(row.id)}
                                                    className="hover:text-red-600"
                                                >
                                                    <FiTrash2 className="text-xs" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Dotted Bottom Footer Trigger Box */}
                <div
                    onClick={handleAddNewCheck}
                    className="p-4 bg-slate-50/20 border-t border-dashed border-slate-200 hover:bg-slate-50/50 cursor-pointer transition-colors group flex items-start gap-3"
                >
                    <div className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-blue-200 bg-white text-blue-600 group-hover:scale-105 transition-transform mt-0.5">
                        <FiPlus className="text-sm" />
                    </div>
                    <div>
                        <span className="text-xs font-bold text-blue-600">Add new check</span>
                        <p className="text-[11px] text-slate-400 mt-0.5">Add additional checks that are specific to your restaurant.</p>
                    </div>
                </div>
            </div>

            {/* --- CRUD MODAL OVERLAY --- */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-4">
                    <div className="bg-white rounded-xl border border-slate-100 shadow-xl w-full max-w-md overflow-hidden font-sans animate-in fade-in zoom-in-95 duration-150">

                        {/* Modal Header */}
                        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-sm font-bold text-slate-900">
                                {modalMode === 'add' ? 'Add New Check' : 'Edit Check Item'}
                            </h3>
                            <button
                                type="button"
                                onClick={handleCloseModal}
                                className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-50 transition-colors"
                            >
                                <FiX className="text-base" />
                            </button>
                        </div>

                        {/* Modal Form */}
                        <form onSubmit={handleSaveCheck}>
                            <div className="p-4 space-y-4">
                                <div>
                                    <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                                        Check Title *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formTitle}
                                        onChange={(e) => setFormTitle(e.target.value)}
                                        placeholder="e.g., Check kitchen extraction fans"
                                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-hidden focus:border-blue-500 text-slate-800 placeholder-slate-400 bg-white transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                                        Description
                                    </label>
                                    <textarea
                                        rows="3"
                                        value={formDescription}
                                        onChange={(e) => setFormDescription(e.target.value)}
                                        placeholder="Provide specific notes or steps for this check..."
                                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-hidden focus:border-blue-500 text-slate-800 placeholder-slate-400 bg-white transition-colors resize-none"
                                    />
                                </div>
                            </div>

                            {/* Modal Footer Actions */}
                            <div className="p-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!formTitle.trim()}
                                    className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {modalMode === 'add' ? 'Create Check' : 'Save Changes'}
                                </button>
                            </div>
                        </form>

                    </div>
                </div>
            )}
        </div>
    );
}
